import { Workbox } from 'workbox-window';

import { syncStore } from '../stores/sync-store.js';

/**
 * Phase 10 — register the Workbox-generated service worker and surface
 * "update available" via the global sync store. We don't auto-reload
 * because doing so during an in-progress inspection or work-order edit
 * would silently lose unsaved state — the user clicks the toast and
 * `skipWaiting` runs only then.
 */
export function registerServiceWorker(): void {
  if (import.meta.env.DEV) return;
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  const wb = new Workbox('/sw.js');

  let reloading = false;
  const reloadOnce = () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  };

  wb.addEventListener('waiting', () => {
    syncStore.getState().setUpdateReady(true);
  });

  wb.addEventListener('controlling', reloadOnce);

  syncStore.getState().setApplyUpdate(() => {
    void wb.messageSkipWaiting();
    // Reload even when the waiting worker never claims this tab.
    window.setTimeout(reloadOnce, 500);
  });

  wb.register().catch((err) => {
    console.warn('[pwa] failed to register service worker', err);
  });
}

/**
 * Captures the `beforeinstallprompt` event for browsers that support
 * the install banner (Chrome/Edge). The captured event is stored so
 * the UI can show an "Install SiteWise" button at any time.
 */
export function bindInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    syncStore.getState().setInstallPrompt(event as unknown as BeforeInstallPromptEvent);
  });
  window.addEventListener('appinstalled', () => {
    syncStore.getState().setInstallPrompt(null);
  });
}

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}
